export const ASSIGNMENT_STATUSES = ['Pending', 'In Progress', 'Completed'];

export const ASSIGNMENT_TYPES = [
  'GST Refund',
  'GST Return',
  'Income Tax Return',
  'TDS Return',
  'Balance Sheet Audit',
  'Tax Audit',
  'Statutory Audit',
  'Internal Audit',
  'Concurrent Audit',
  'GST Notice',
  'Income Tax Notice',
  'GST Appeal',
  'Income Tax Appeal',
  'ROC Filing',
  'Certificate',
  'GST Registration',
  'Compliance',
  'Other',
];

export const EMPTY_ASSIGNMENT = {
  clientId: '',
  assignmentType: '',
  financialYear: '',
  period: '',
  assignedToId: '',
  startDate: '',
  dueDate: '',
  remarks: '',
  status: 'Pending',
};

export function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return new Date(value.getTime());

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function startDateOf(assignment) {
  if (!assignment) return null;
  return assignment.startDate ?? assignment.fromDate ?? null;
}

export function dueDateOf(assignment) {
  if (!assignment) return null;
  return assignment.dueDate ?? assignment.endDate ?? assignment.deadline ?? null;
}

export function toInputDate(value) {
  const date = toDate(value);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDate(value) {
  const date = toDate(value);
  return date ? date.toLocaleDateString('en-IN') : '—';
}

export function assignmentTypeOf(assignment) {
  if (!assignment) return '';
  return assignment.assignmentType || assignment.category || assignment.taskType || '';
}

export function financialYearOf(assignment) {
  if (!assignment) return '';
  return assignment.financialYear || assignment.fy || '';
}

export function normalizedStatus(assignment) {
  const raw = String(assignment?.status || 'Pending').trim().toLowerCase();
  if (raw === 'completed' || raw === 'complete') return 'Completed';
  if (raw === 'in progress' || raw === 'in-progress' || raw === 'inprogress') return 'In Progress';
  return 'Pending';
}

export function displayName(person) {
  return person?.name || person?.username || person?.email || '';
}

export function isActiveStaff(person) {
  const status = String(person?.status || '').toLowerCase();
  if (person?.isActive === false || status === 'inactive') return false;
  return true;
}

export function isCompleted(assignment) {
  return normalizedStatus(assignment) === 'Completed';
}

export function dueState(assignment) {
  if (!assignment) return 'none';
  if (isCompleted(assignment)) return 'completed';

  const due = toDate(dueDateOf(assignment));
  if (!due) return 'none';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);

  if (due.getTime() < today.getTime()) return 'overdue';
  if (due.getTime() === today.getTime()) return 'today';
  return 'upcoming';
}

export function normalizeAssignmentForForm(assignment) {
  const safeAssignment = assignment || {};
  return {
    ...EMPTY_ASSIGNMENT,
    ...safeAssignment,
    assignmentType: assignmentTypeOf(safeAssignment),
    financialYear: financialYearOf(safeAssignment),
    startDate: toInputDate(startDateOf(safeAssignment)),
    dueDate: toInputDate(dueDateOf(safeAssignment)),
    status: normalizedStatus(safeAssignment),
  };
}

export function validateAssignment(values) {
  const errors = {};
  if (!values?.clientId) errors.clientId = 'Client is required.';
  if (!values?.assignmentType?.trim()) errors.assignmentType = 'Assignment Type is required.';
  if (!values?.assignedToId) errors.assignedToId = 'Assigned To is required.';
  if (!values?.dueDate) errors.dueDate = 'Due Date is required.';
  if (!ASSIGNMENT_STATUSES.includes(values?.status)) errors.status = 'Select a valid status.';
  if (values?.startDate && values?.dueDate && values.startDate > values.dueDate) {
    errors.dueDate = 'Due Date cannot be before Start Date.';
  }
  return errors;
}
